import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Download, ChevronDown, FileText, FileSpreadsheet, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useEditor } from "@/lib/store";
import { getProject, updateProject } from "@/lib/api";
import LibraryPanel from "@/components/editor/LibraryPanel";
import PropertiesPanel from "@/components/editor/PropertiesPanel";
import Toolbar from "@/components/editor/Toolbar";
import Canvas2D from "@/components/editor/Canvas2D";
import Canvas3D from "@/components/editor/Canvas3D";
import html2canvas from "html2canvas";
import { exportPDF } from "@/lib/export/pdf";
import { exportBoQCsv } from "@/lib/export/bom";

export default function Editor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const exportRef = useRef(null);

  const view = useEditor((s) => s.view);
  const projectName = useEditor((s) => s.projectName);
  const clientName = useEditor((s) => s.clientName);
  const loadProject = useEditor((s) => s.loadProject);
  const exportPayload = useEditor((s) => s.exportPayload);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const p = await getProject(id);
        loadProject(p);
        setReady(true);
      } catch (e) {
        toast.error("Project not found");
        nav("/dashboard", { replace: true });
      }
    })();
  }, [id, loadProject, nav]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updateProject(id, exportPayload());
      toast.success("Project saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }, [id, exportPayload]);

  // Auto-save every 30s
  useEffect(() => {
    if (!ready) return;
    const t = setInterval(() => { handleSave(); }, 30000);
    return () => clearInterval(t);
  }, [ready, handleSave]);

  async function handleExportPng() {
    const node = exportRef.current;
    if (!node) return;
    try {
      const canvas = await html2canvas(node, { backgroundColor: "#f7f4ee", scale: 2, useCORS: true });
      const link = document.createElement("a");
      link.download = `${projectName || "layout"}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("PNG exported");
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    }
  }

  async function handleExportPdf() {
    try {
      toast.info("Building PDF report…");
      await exportPDF(exportPayload(), exportRef);
      toast.success("PDF report exported");
    } catch (e) {
      console.error(e);
      toast.error("PDF export failed");
    }
  }

  function handleExportBoq() {
    try {
      const safe = (projectName || "layout").replace(/[^a-z0-9-_]+/gi, "_");
      exportBoQCsv(exportPayload(), `${safe}_BoQ.csv`);
      toast.success("BoQ CSV exported");
    } catch (e) {
      console.error(e);
      toast.error("BoQ export failed");
    }
  }

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground text-sm font-mono">Loading project…</div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-background text-foreground overflow-hidden">
      {/* TOP BAR */}
      <header className="h-12 border-b border-border/60 flex items-center px-3 gap-3 shrink-0">
        <button
          data-testid="back-to-dashboard"
          onClick={() => nav("/dashboard")}
          className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-xs font-mono uppercase tracking-widest">back</span>
        </button>
        <div className="w-px h-5 bg-border/60" />
        <div className="flex items-center gap-2.5">
          <div className="leading-tight">
            <div className="text-sm font-semibold tracking-tight" data-testid="editor-project-name">{projectName}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              {clientName || "no client"}
            </div>
          </div>
        </div>
        <div className="flex-1" />
        <Button
          data-testid="save-project-btn"
          onClick={handleSave}
          disabled={saving}
          className="h-8 rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground text-xs uppercase tracking-widest font-mono"
        >
          <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              data-testid="export-menu-btn"
              variant="outline"
              className="h-8 rounded-sm text-xs uppercase tracking-widest font-mono ml-1"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export
              <ChevronDown className="w-3 h-3 ml-1 opacity-70" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-sm min-w-[180px]">
            <DropdownMenuItem data-testid="export-pdf-item" onClick={handleExportPdf} className="cursor-pointer">
              <FileText className="w-3.5 h-3.5 mr-2" />
              <span className="text-xs">PDF report</span>
              <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-muted-foreground">+BoQ</span>
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="export-boq-item" onClick={handleExportBoq} className="cursor-pointer">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-2" />
              <span className="text-xs">Bill of Quantities (CSV)</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="export-png-item" onClick={handleExportPng} className="cursor-pointer">
              <ImageIcon className="w-3.5 h-3.5 mr-2" />
              <span className="text-xs">Canvas snapshot (PNG)</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* MAIN GRID */}
      <div className="flex-1 flex min-h-0">
        <LibraryPanel />
        <div className="flex-1 flex flex-col min-w-0 min-h-0 relative">
          <Toolbar />
          <div ref={exportRef} className="flex-1 flex min-h-0 bg-white">
            {(view === "2d" || view === "split") && (
              <div data-canvas-2d className={view === "split" ? "flex-1 min-w-0 border-r border-border/60" : "flex-1 min-w-0"}>
                <Canvas2D />
              </div>
            )}
            {(view === "3d" || view === "split") && (
              <div data-canvas-3d className={view === "split" ? "flex-1 min-w-0" : "flex-1 min-w-0"}>
                <Canvas3D />
              </div>
            )}
          </div>
        </div>
        <PropertiesPanel />
      </div>
    </div>
  );
}
